import type { TaskAssigneeUser } from "@components/modules/resource-planning/tasks/components/task-assignees/types";
import type {
  TaskAssigneesEditableTask,
  TaskAssigneesUpdate,
} from "@components/modules/resource-planning/tasks/components/task-assignees/variants/editable/types";
import type { TaskBillingUpdate } from "@components/modules/resource-planning/tasks/components/task-billing/variants/editable/types";
import type { TaskDeadlineUpdate } from "@components/modules/resource-planning/tasks/components/task-deadline/variants/editable/types";
import type { TaskDurationUpdate } from "@components/modules/resource-planning/tasks/components/task-duration/variants/editable/types";
import type { TaskTrackedSummary } from "@components/modules/resource-planning/tasks/components/task-tracked-progress/types";
import { taskTrackedSummary } from "@components/modules/resource-planning/tasks/components/task-tracked-progress/utils";
import type { TaskDetailModalStore } from "@components/widgets/Modals/ModalCommunication/store/task-detail-store";
import { t } from "@lingui/macro";
import {
  type TaskPositionInput,
  type TaskPriority,
  UpdateTaskAssigneesDocument,
  type UpdateTaskAssigneesMutation,
  type UpdateTaskAssigneesMutationVariables,
  UpdateTaskDocument,
  type UpdateTaskInput,
  type UpdateTaskMutation,
  type UpdateTaskMutationVariables,
  UpdateTaskPriorityDocument,
  type UpdateTaskPriorityMutation,
  type UpdateTaskPriorityMutationVariables,
  type User,
} from "@src/__generated__/graphql";
import type {
  AssignedUser,
  TaskModel,
} from "@src/components/widgets/Modals/ModalCommunication/models/Task";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { ValueState } from "@src/utils/mobx/states/ValueState";
import { uniqBy } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";

/** Positions are keyed by user and work type, a refetch may reorder them. */
export type TaskPropertyEditor = `position-${string}`;

export type AssigneePosition = {
  assignee: AssignedUser;
  editor: TaskPropertyEditor;
  isCurrent: boolean;
};

export class TaskOverviewStore {
  appStore: AppStore;
  detailStore: TaskDetailModalStore;

  @observable.ref openEditor: TaskPropertyEditor | null = null;

  allocationDraft = new ValueState<number | undefined>(undefined);
  workTypeDraft = new ValueState<string | undefined>(undefined);
  /** Only editable on an unassigned position, a user's own one keeps them. */
  userDraft = new ValueState<User["id"] | undefined>(undefined);

  isAssigneesExpanded = new BooleanState(false);

  private updateTaskMutator = new MutationHelper<
    UpdateTaskMutation,
    UpdateTaskMutationVariables
  >(UpdateTaskDocument);
  private updatePriorityMutator = new MutationHelper<
    UpdateTaskPriorityMutation,
    UpdateTaskPriorityMutationVariables
  >(UpdateTaskPriorityDocument);
  private updateAssigneesMutator = new MutationHelper<
    UpdateTaskAssigneesMutation,
    UpdateTaskAssigneesMutationVariables
  >(UpdateTaskAssigneesDocument);

  constructor(appStore: AppStore, detailStore: TaskDetailModalStore) {
    makeObservable(this);
    this.appStore = appStore;
    this.detailStore = detailStore;
  }

  @computed get task(): TaskModel | null {
    return this.detailStore.task.value;
  }

  @computed get canEdit(): boolean {
    return (
      this.detailStore.isInternalUser && this.detailStore.permissions.canUpdate
    );
  }

  @computed get canEditSchedule(): boolean {
    return this.canEdit && !this.task?.is_template;
  }

  @computed get canSetCurrentAssignee(): boolean {
    return !!this.appStore.workspaceStore.settings?.use_current_assignee;
  }

  @computed get isUpdatingPriority(): boolean {
    return this.updatePriorityMutator.isLoading.value;
  }

  @computed get isUpdatingAssignees(): boolean {
    return this.updateAssigneesMutator.isLoading.value;
  }

  @computed get trackedSummary(): TaskTrackedSummary {
    return taskTrackedSummary({
      tracked: this.task?.stats?.spent_time ?? 0,
      budget: this.task?.stats?.planned_time ?? 0,
      unassigned: this.task?.otherPeopleTrackedTotal ?? 0,
    });
  }

  @computed get internalAssignees(): AssignedUser[] {
    return this.task?.assignedUsers.filter(({ user }) => !user?.scope) ?? [];
  }

  @computed get scopedAssignees(): AssignedUser[] {
    return this.task?.assignedUsers.filter(({ user }) => !!user?.scope) ?? [];
  }

  @computed get currentAssigneeId(): User["id"] | null {
    if (!this.canSetCurrentAssignee) return null;
    return this.task?.currentAssignee?.id ?? null;
  }

  @computed get assigneeUsers(): TaskAssigneeUser[] {
    return uniqBy(
      this.internalAssignees.flatMap(({ user }) => toAssigneeUser(user)),
      "id",
    );
  }

  @computed get currentAssignee(): TaskAssigneeUser | null {
    if (!this.currentAssigneeId) return null;

    return (
      this.assigneeUsers.find(({ id }) => id === this.currentAssigneeId) ?? null
    );
  }

  @computed get scopedUsers(): TaskAssigneeUser[] {
    return this.scopedAssignees.flatMap(({ user }) => toAssigneeUser(user));
  }

  /** The task in the shape `TaskAssigneesEditable` reads. */
  @computed get assigneesTask(): TaskAssigneesEditableTask | null {
    const task = this.task;
    if (!task) return null;

    return {
      id: task.id,
      projectId: task.ourWorkBudgetItem?.project.id,
      assignees: this.assigneeUsers,
      currentAssignee: this.currentAssignee,
    };
  }

  @computed private get assigneePositions(): AssigneePosition[] {
    return this.internalAssignees.map((assignee) => ({
      assignee,
      editor: positionEditor(assignee),
      isCurrent:
        !!this.currentAssigneeId &&
        assignee.user?.id === this.currentAssigneeId,
    }));
  }

  @computed get currentAssigneePositions(): AssigneePosition[] {
    return this.assigneePositions.filter(({ isCurrent }) => isCurrent);
  }

  @computed get otherAssigneePositions(): AssigneePosition[] {
    return this.assigneePositions.filter(({ isCurrent }) => !isCurrent);
  }

  isEditorOpen = (editor: TaskPropertyEditor): boolean => {
    return this.openEditor === editor;
  };

  @action.bound
  reset(): void {
    this.openEditor = null;
    this.isAssigneesExpanded.off();
  }

  @action.bound
  openEditorFor(editor: TaskPropertyEditor): void {
    if (this.openEditor === editor) {
      this.closeEditor();
      return;
    }

    this.openEditor = editor;
  }

  @action.bound
  closeEditor(): void {
    this.openEditor = null;
  }

  @action.bound
  async commitTitle(text: string): Promise<void> {
    const name = text.trim();
    if (!name || name === this.task?.name) return;

    const task = this.task;
    if (!task) return;

    const previousName = task.name;
    task.setName(name);

    const isUpdated = await this.updateTask({ name });
    if (!isUpdated) task.setName(previousName);
  }

  /** Syncs dates saved by `TaskDeadlineEditable` or `TaskDurationEditable`. */
  @action.bound
  handleScheduleUpdated(
    updated: TaskDeadlineUpdate | TaskDurationUpdate,
  ): void {
    this.task?.setSchedule({
      from: updated.from,
      to: updated.to,
      deadline: updated.deadline,
    });
    this.refreshTask();
  }

  /** Syncs a change saved by `TaskAssigneesEditable`. */
  @action.bound
  handleAssigneesUpdated(updated: TaskAssigneesUpdate): void {
    this.task?.setCurrentAssignee(updated.currentAssignee?.id ?? null);
    // Positions come back in the board's shape, the refetch reloads them
    this.refreshTask();
  }

  /** Syncs a change saved by `TaskBillingEditable`. */
  @action.bound
  handleBillableUpdated(updated: TaskBillingUpdate): void {
    this.task?.setBillable(updated.billable);
    this.refreshTask();
  }

  /** Opens the editor of the user's first position, under the expanded pills. */
  @action.bound
  openAssigneeEditor(userId: User["id"]): void {
    const position = this.assigneePositions.find(
      ({ assignee }) => assignee.user?.id === userId,
    );
    if (!position) return;

    this.isAssigneesExpanded.on();
    this.openPositionEditor(position.editor);
  }

  @action.bound
  async updatePriority(priorityId: TaskPriority["id"]): Promise<void> {
    const task = this.task;
    if (!task?.id) return;

    const [data, error] = await this.updatePriorityMutator.mutate({
      task_id: task.id,
      priority_id: priorityId,
    });
    if (error) return;

    task.setPriority(data.updateTaskPriority.priority);
    this.refreshTask();
  }

  @action.bound
  async pickCurrentAssignee(userId: User["id"] | null): Promise<void> {
    this.closeEditor();
    const isChanged = await this.detailStore.setCurrentAssignee(userId);
    if (isChanged) this.refreshTask();
  }

  @action.bound
  openPositionEditor(editor: TaskPropertyEditor): void {
    const assignee = this.positionAssignee(editor);
    if (!this.canEdit || !assignee) return;

    this.allocationDraft.set(Number(assignee.planned_time ?? 0));
    this.workTypeDraft.set(assignee.timeTrackingWorkType?.id);
    this.userDraft.set(assignee.user?.id ?? undefined);
    this.openEditorFor(editor);
  }

  @action.bound
  async savePositionAllocation(editor: TaskPropertyEditor): Promise<void> {
    const assignee = this.positionAssignee(editor);
    const workTypeId = this.workTypeDraft.value;
    if (!assignee || !workTypeId) return;

    const userId = assignee.user?.id ?? this.userDraft.value ?? null;
    const isSameUserAndWorkType = (position: TaskPositionInput) =>
      position.time_tracking_work_type_id === workTypeId &&
      (position.user_id ?? null) === userId;
    const rest = this.positionsInput.filter(
      (position) => !this.isSamePosition(position, assignee),
    );
    // A work type the (newly picked) user already has merges both allocations
    const merged = rest.find(isSameUserAndWorkType);
    const edited: TaskPositionInput = {
      time_tracking_work_type_id: workTypeId,
      planned_time:
        (this.allocationDraft.value ?? 0) + (merged?.planned_time ?? 0),
      user_id: userId,
    };

    await this.updatePositions([
      ...rest.filter((position) => !isSameUserAndWorkType(position)),
      edited,
    ]);
  }

  @action.bound
  async removePosition(editor: TaskPropertyEditor): Promise<void> {
    const assignee = this.positionAssignee(editor);
    if (!assignee) return;

    await this.updatePositions(
      this.positionsInput.filter(
        (position) => !this.isSamePosition(position, assignee),
      ),
    );
  }

  @computed private get positionsInput(): TaskPositionInput[] {
    return (this.task?.positions ?? []).flatMap((position) => {
      if (!position.timeTrackingWorkType) return [];

      return [
        {
          time_tracking_work_type_id: position.timeTrackingWorkType.id,
          planned_time: position.planned_time ?? 0,
          user_id: position.user?.id,
        },
      ];
    });
  }

  private positionAssignee(editor: TaskPropertyEditor): AssignedUser | null {
    return (
      this.internalAssignees.find(
        (assignee) => positionEditor(assignee) === editor,
      ) ?? null
    );
  }

  /**
   * Tells the opener about the change. The refetch also refreshes the
   * revision, so the stale-task banner does not fire for our own edit.
   */
  private refreshTask(): void {
    void this.detailStore.fetchTask({ silently: true, notifyOpener: true });
  }

  private isSamePosition(
    position: TaskPositionInput,
    assignee: AssignedUser,
  ): boolean {
    return (
      position.time_tracking_work_type_id ===
        assignee.timeTrackingWorkType?.id &&
      (position.user_id ?? null) === (assignee.user?.id ?? null)
    );
  }

  private async updatePositions(
    positions: TaskPositionInput[],
  ): Promise<boolean> {
    if (!this.task?.id) return false;

    const [, error] = await this.updateAssigneesMutator.mutate({
      taskId: this.task.id,
      positions,
    });
    if (error) return false;

    this.closeEditor();
    this.appStore.UIStore.toast({ title: t`Users updated`, status: "success" });
    this.refreshTask();
    return true;
  }

  private async updateTask(
    input: Omit<UpdateTaskInput, "id" | "is_template">,
  ): Promise<boolean> {
    if (!this.task?.id) return false;

    const [, error] = await this.updateTaskMutator.mutate({
      input: {
        id: this.task.id,
        is_template: this.task.is_template,
        ...input,
      },
      propagate_billable: false,
    });
    if (error) return false;

    this.refreshTask();
    return true;
  }
}

function positionEditor(assignee: AssignedUser): TaskPropertyEditor {
  return `position-${assignee.user?.id ?? "none"}-${assignee.timeTrackingWorkType?.id}`;
}

/** Clients and partners come without `full_name`, it is built from their names. */
function toAssigneeUser(user: AssignedUser["user"]): TaskAssigneeUser[] {
  if (!user?.id) return [];

  const fullName =
    user.full_name ??
    [user.first_name, user.last_name].filter(Boolean).join(" ");

  return [{ ...user, id: user.id, full_name: fullName }];
}
